// src/services/clientService.ts
import { api } from './api';

export interface Client {
  _id: string;
  name: string;
  email: string;
  address: string;
  website?: string;
  contactPerson: string;
  phone: string;
  status?: string;
  complianceStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface Location {
  _id: string;
  name: string;
  type: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  longitude: string;
  latitude: string;
  coordinates?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LocationType {
  _id: string;
  name: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssetType {
  _id: string;
  name: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssetCategory {
  _id: string;
  name: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Asset {
  _id: string;
  wellId?: string; // Already present
  wellMnemonic?: string; // Already present
  name: string;
  description?: string;
  serialNo: string;
  assetDocumentation?: string;
  status?: string;
  oilField?: string;
  location?: string | Location;
  type?: string | AssetType;
  category?: string | AssetCategory;
  purchaseDate?: string;
  purchasePrice?: number;
  condition?: string;
  warranteeExpiryDate?: string;
  owner: string;
  longitude?: string;
  latitude?: string;
  // Oil well specific fields
  wellType?: string;
  easting?: string;
  northing?: string;
  spudDate?: string;
  initialCompletion?: string;
  firstProduction?: string;
  td?: string;
  originalDFE?: string;
  odfeChh?: string;
  flowStation?: string;
  measuredDepth?: string;
  verticalDepth?: string;
  flowLine?: {
    ls: string;
    ss: string;
  };
  tubing?: {
    ls: string;
    ss: string;
  };
  requireCompliance?: boolean;
  isCompliant?: boolean;
  complianceInformation?: Array<{
    _id?: string;
    checklist: string;
    frequency: string;
    description: string;
    nextDate: string;
    isRecurring: boolean;
  }>;
  scheduledMaintenance?: Array<{
    maintenanceName: string;
    maintenanceFrequency: string;
    maintenanceDescription: string;
    nextDate: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
}


// Define the clients data with pagination
interface ClientsData {
  result: Client[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface LocationsData {
  result: Location[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface AssetsData {
  result: Asset[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface ClientResponse {
  message: string;
  data: Client;
  error: boolean;
}

interface ClientsResponse {
  message: string;
  data: ClientsData | Client[];
  error: boolean;
}

interface LocationResponse {
  message: string;
  data: Location;
  error: boolean;
}

interface LocationsResponse {
  message: string;
  data: LocationsData | Location[];
  error: boolean;
}

interface AssetResponse {
  message: string;
  data: Asset;
  error: boolean;
}

interface AssetsResponse {
  message: string;
  data: AssetsData | Asset[];
  error: boolean;
}

export interface ContactPersonEntry {
  name?: string;
  title?: string;
  email?: string;
  phone?: string;
}

export interface ClientFormData {
  name: string;
  email: string;
  password: string;
  address?: string;
  website?: string;
  contactPerson?: string;
  phone?: string;
  contacts?: ContactPersonEntry[];
}

export interface ClientUpdateData {
  name?: string;
  email?: string;
  password?: string;
  address?: string;
  website?: string;
  contactPerson?: string;
  phone?: string;
  status?: string;
}

export interface FieldContactInfo {
  name?: string;
  title?: string;
  email?: string;
  phone?: string;
}

export interface LocationFormData {
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  longitude?: string;
  latitude?: string;
  type?: string;
  communityLiaison?: FieldContactInfo;
  fieldSuperintendent?: FieldContactInfo;
}

// Updated AssetFormData to include all possible fields
export interface AssetFormData {
  name: string;
  description?: string;
  serialNo: string;
  assetDocumentation?: string;
  status?: string;
  oilField?: string;
  location: string;
  type?: string;
  category?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  condition?: string;
  warranteeExpiryDate?: string;
  owner: string;
  longitude?: string;
  latitude?: string;
  // Oil well specific fields
  wellType?: string;
  wellMnemonic?: string; // Already present
  wellId?: string; // Add this field
  easting?: string;
  northing?: string;
  spudDate?: string;
  initialCompletion?: string;
  firstProduction?: string;
  td?: string;
  originalDFE?: string;
  odfeChh?: string;
  flowStation?: string;
  measuredDepth?: string;
  verticalDepth?: string;
  flowLine?: {
    ls: string;
    ss: string;
  };
  tubing?: {
    ls: string;
    ss: string;
  };
  requireCompliance?: boolean;
  complianceInformation?: Array<{
    checklist: string;
    frequency: string;
    description: string;
    nextDate: string;
    isRecurring: boolean;
  }>;
  scheduledMaintenance?: Array<{
    maintenanceName: string;
    maintenanceFrequency: string;
    maintenanceDescription: string;
    nextDate: string;
  }>;
}

export interface LocationTypeFormData {
  name: string;
  description: string;
  _id?: string; // Make _id optional since it only exists in responses, not requests
}

// If you have a separate type for POST requests, you might want to structure it like:
export interface LocationTypeCreateData {
  name: string;
  description: string;
  // other fields needed for creation without _id
}

export interface AssetTypeFormData {
  name: string;
  description: string;
}

export interface ApiResponse<T> {
  message: string;
  data: T;
  error: boolean;
}

export const clientService = {
  async getAllClients(page = 1, limit = 10): Promise<ClientsData | Client[]> {
    const response = await api.get<ClientsResponse>(`/user/client/all?page=${page}&limit=${limit}`);
    return response.data.data;
  },
  
  async getClient(id: string): Promise<Client> {
    const response = await api.get<ClientResponse>(`/user/client/${id}`);
    return response.data.data;
  },
  
  async createClient(clientData: ClientFormData): Promise<Client> {
    const response = await api.post<ClientResponse>('/user/client/create', clientData);
    return response.data.data;
  },
  
  async updateClient(id: string, clientData: ClientUpdateData): Promise<Client> {
    const response = await api.put<ClientResponse>(`/user/client/${id}`, clientData);
    return response.data.data;
  },
  
  async deleteClient(id: string): Promise<void> {
    await api.delete(`/user/${id}`);
  },

  // New methods for locations
  async getClientLocations(clientId: string, page = 1, limit = 10): Promise<LocationsData | Location[]> {
    const response = await api.get<LocationsResponse>(`/asset-location/client/${clientId}?page=${page}&limit=${limit}`);
    return response.data.data;
  },

  async getLocationDetails(locationId: string): Promise<Location> {
    const response = await api.get<LocationResponse>(`/asset-location/${locationId}`);
    return response.data.data;
  },

  async createLocation(locationData: LocationFormData): Promise<Location> {
    const response = await api.post<LocationResponse>('/asset-location', locationData);
    return response.data.data;
  },

  async updateLocation(id: string, locationData: LocationFormData): Promise<Location> {
    const response = await api.put<LocationResponse>(`/asset-location/${id}`, locationData);
    return response.data.data;
  },

  async deleteLocation(locationId: string): Promise<void> {
    await api.delete(`/asset-location/${locationId}`);
  },

  // New methods for location types
  async createLocationType(typeData: LocationTypeFormData): Promise<ApiResponse<LocationType>> {
    const response = await api.post<ApiResponse<LocationType>>('/location-type', typeData);
    return response.data;
  },

  async getLocationTypes(page = 1, limit = 100): Promise<any> {
    const response = await api.get<{ 
      message: string; 
      data: { 
        result: LocationType[],
        totalCount: number,
        currentPage: number,
        totalPages: number,
        previousPage: number | null,
        nextPage: number | null
      }; 
      error: boolean 
    }>(`/location-type?page=${page}&limit=${limit}`);
    
    return response.data;
  },

  // New methods for assets
  async getLocationAssets(locationId: string, page = 1, limit = 10): Promise<AssetsData | Asset[]> {
    const response = await api.get<AssetsResponse>(`/asset/location/${locationId}?page=${page}&limit=${limit}`);
    return response.data.data;
  },

  async createAsset(assetData: AssetFormData): Promise<Asset> {
    const response = await api.post<AssetResponse>('/asset', assetData);
    return response.data.data;
  },

  async updateAsset(id: string, assetData: AssetFormData): Promise<Asset> {
    const response = await api.put<AssetResponse>(`/asset/${id}`, assetData);
    return response.data.data;
  },

  async deleteAsset(id: string): Promise<void> {
    await api.delete(`/asset/${id}`);
  },

  // New methods for asset types
  async createAssetType(typeData: AssetTypeFormData): Promise<AssetType> {
    const response = await api.post<{ message: string; data: AssetType; error: boolean }>('/asset-type', typeData);
    return response.data.data;
  },

  async createAssetCategory(categoryData: AssetCategory): Promise<AssetCategory> {
    const response = await api.post<{ message: string; data: AssetCategory; error: boolean }>('/asset-category', categoryData);
    return response.data.data;
  },

  async getAssetCategory(page = 1, limit = 100): Promise<ApiResponse<{ 
    result: AssetCategory[],
    totalCount: number,
    currentPage: number,
    totalPages: number,
    previousPage: number | null,
    nextPage: number | null
  }>> {
    const response = await api.get<{ 
      message: string; 
      data: { 
        result: AssetCategory[],
        totalCount: number,
        currentPage: number,
        totalPages: number,
        previousPage: number | null,
        nextPage: number | null
      }; 
      error: boolean 
    }>(`/asset-category?page=${page}&limit=${limit}`);
    
    // Return the entire response structure
    return response.data;
  },

  async getAssetTypes(page = 1, limit = 100): Promise<any> {
    const response = await api.get<{ 
      message: string; 
      data: {
        result: AssetType[],
        totalCount: number,
        currentPage: number,
        totalPages: number,
        previousPage: number | null,
        nextPage: number | null
      }; 
      error: boolean 
    }>(`/asset-type?page=${page}&limit=${limit}`);
    return response.data;
  },

  // New methods for fetching all assets and single asset
  async getAllAssets(page = 1, limit = 10): Promise<AssetsData | Asset[]> {
    const response = await api.get<AssetsResponse>(`/asset?page=${page}&limit=${limit}`);
    return response.data.data;
  },

  async getAsset(id: string): Promise<Asset> {
    const response = await api.get<AssetResponse>(`/asset/${id}`);
    return response.data.data;
  },

  // Add this new method for fetching all asset locations
  async getAssetLocations(page = 1, limit = 100): Promise<Location[]> {
    const response = await api.get<{ 
      message: string; 
      data: { 
        result: Location[],
        totalCount: number,
        currentPage: number,
        totalPages: number,
        previousPage: number | null,
        nextPage: number | null
      }; 
      error: boolean 
    }>(`/asset-location?page=${page}&limit=${limit}`);
    
    return response.data.data.result;
  },

  // Add delete methods for types and categories
  async deleteAssetType(id: string): Promise<void> {
    await api.delete(`/asset-type/${id}`);
  },

  async deleteLocationType(id: string): Promise<void> {
    await api.delete(`/location-type/${id}`);
  },

  async deleteAssetCategory(id: string): Promise<void> {
    await api.delete(`/asset-category/${id}`);
  },

  // Add this new method for fetching assets by client
  async getAssetsByClient(clientId: string, page = 1, limit = 10): Promise<AssetsData | Asset[]> {
    const response = await api.get<AssetsResponse>(`/asset/client/${clientId}?page=${page}&limit=${limit}`);
    return response.data.data;
  },

  async getAssetsByFlowStation(flowStationId: string, page = 1, limit = 10): Promise<AssetsData | Asset[]> {
    const response = await api.get<AssetsResponse>(`/asset/flow-station/${flowStationId}?page=${page}&limit=${limit}`);
    return response.data.data;
  },

  async getFlowStationsByLocation(locationId: string, page = 1, limit = 100): Promise<any> {
    const response = await api.get(`/flow-station/location/${locationId}?page=${page}&limit=${limit}`);
    return response.data.data;
  },

  async updateCustomer(id: string, customerData: Partial<ClientUpdateData>): Promise<Client> {
    const response = await api.put<ClientResponse>(`/user/client/${id}`, customerData);
    return response.data.data;
  },

  // Add bulk upload method
  async bulkUploadAssets(file: File, owner: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    // formData.append('owner', owner);

    const response = await api.post('/asset/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },
};

// New function to get asset by ID with maintenance and archive data
export const getAssetById = async (assetId: string) => {
  try {
    const response = await api.get(`/asset/${assetId}`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error fetching asset by ID:', error);
    throw error;
  }
};

export const getClientById = async (clientId: string) => {
  try {
    const response = await api.get(`/user/${clientId}`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error fetching client by ID:', error);
    throw error;
  }
};

export const updateClient = async (id: string, clientData: Partial<ClientFormData>): Promise<ApiResponse<Client>> => {
  try {
    const response = await api.put(`/user/${id}`, clientData);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update client');
  }
};